import { User, UserPlain } from '../../../../common/iam/users/types';
import { CompanyIdAndName } from '../../../../common/model/input/companies/types';
import { ImpactSizeFactorByImpactCodeAndValence } from '../../../../common/model/input/impact-size-factors/types';
import { ImpactLabelDatum } from '../../../../common/model/input/impacts/types';
import { PredictedLearningSample } from '../../../../common/model/input/machine-learning/types-machine-learning';
import { ModelEntityReference } from '../../../../common/model/input/types';
import { ValueSet } from '../../../../common/model/input/value-sets/types';
import { GlobalFigures } from '../../../../common/model/output/market-sizes/types';
import { ReferenceFigures } from '../../../../common/model/output/scores/reference-figures/types';
import { ImpactScoresByPIVV } from '../../../../common/model/output/scores/types';
import { ToggledUIComponents } from '../../../common/generic/utilities/types';
import { ViewSettingKey } from '../../../common/generic/view-settings/registry';
import { ContributionAction } from '../modules/contribution';
import { ModalAction } from '../modules/modal';

interface StoreGlobalFiguresAction {
  type: 'STORE_GLOBAL_FIGURES';
  payload: {
    figures: GlobalFigures;
  };
}

interface StoreImpactSizeFactorsAction {
  type: 'STORE_IMPACT_SIZE_FACTORS';
  payload: {
    factors?: ImpactSizeFactorByImpactCodeAndValence;
  };
}
export function storeImpactSizeFactors(factors?: ImpactSizeFactorByImpactCodeAndValence): StoreImpactSizeFactorsAction {
  return {
    type: 'STORE_IMPACT_SIZE_FACTORS',
    payload: {
      factors,
    },
  };
}

interface StoreReferenceFiguresAction {
  type: 'STORE_REFERENCE_FIGURES';
  payload: {
    figures: ReferenceFigures;
  };
}
export function storeReferenceFigures(figures: ReferenceFigures): StoreReferenceFiguresAction {
  return {
    type: 'STORE_REFERENCE_FIGURES',
    payload: {
      figures,
    },
  };
}

interface StoreCompaniesAction {
  type: 'STORE_COMPANIES';
  payload: {
    companies: CompanyIdAndName[];
  };
}

interface AddCompaniesAction {
  type: 'ADD_COMPANIES';
  payload: {
    companies: CompanyIdAndName[];
  };
}
export function addCompanies(companies: CompanyIdAndName[]): AddCompaniesAction {
  return {
    type: 'ADD_COMPANIES',
    payload: {
      companies,
    },
  };
}

interface UpdateCompanyNameAction {
  type: 'UPDATE_COMPANY_NAME';
  payload: {
    company: CompanyIdAndName;
  };
}

interface StoreImpactScoresAction {
  type: 'STORE_IMPACT_SCORES';
  payload: {
    impactScoresByPIVV: ImpactScoresByPIVV;
  };
}

interface StorePredictionsDataAction {
  type: 'STORE_PREDICTIONS_DATA';
  payload: {
    predictions: PredictedLearningSample[];
  };
}

// PLT-4774: STORE_PRODUCTS / REPLACE_PRODUCTS / REMOVE_PRODUCTS were
// removed. The slim product hierarchy is now cached by React Query
// (`useProductsByCode`); mutations invalidate the relevant query keys
// directly.

interface StoreImpactLabelsAction {
  type: 'STORE_IMPACT_LABELS';
  payload: {
    impactLabels: ImpactLabelDatum[];
  };
}

export function storeImpactLabels(impactLabels: ImpactLabelDatum[]): StoreImpactLabelsAction {
  return {
    type: 'STORE_IMPACT_LABELS',
    payload: {
      impactLabels,
    },
  };
}

interface SetCertaintyFilterRangeAction {
  type: 'SET_CERTAINTY_FILTER_RANGE';
  payload: {
    range: [number, number];
  };
}

interface SetCorrectLabelFilterAction {
  type: 'SET_CORRECT_LABEL_FILTER';
  payload: {
    correctLabel?: string;
  };
}

interface SetPredictedLabelFilterAction {
  type: 'SET_PREDICTED_LABEL_FILTER';
  payload: {
    predictedLabelFilter?: {
      label: string;
      classifier: string;
    };
  };
}

interface SetSampleOffsetAction {
  type: 'SET_SAMPLE_OFFSET';
  payload: {
    offset: number;
  };
}

interface SetCertaintyFilterClassifierAction {
  type: 'SET_CERTAINTY_FILTER_CLASSIFIER';
  payload: {
    classifier: string;
  };
}

interface SetSelectedClassifiersAction {
  type: 'SET_SELECTED_CLASSIFIERS';
  payload: {
    classifiers: string[];
  };
}

interface SetValueSetAction {
  type: 'SET_VALUE_SET';
  payload: {
    valueSet: ValueSet;
    name: string;
  };
}

export function setValueSet(valueSet: ValueSet, name: string): SetValueSetAction {
  return {
    type: 'SET_VALUE_SET',
    payload: {
      valueSet,
      name,
    },
  };
}

interface RequestStartedAction {
  type: 'REQUESTS/REQUEST_STARTED';
  payload: {
    id: string;
  };
}
export function requestStarted(id: string): RequestStartedAction {
  return {
    type: 'REQUESTS/REQUEST_STARTED',
    payload: {
      id,
    },
  };
}

export interface RequestFailedAction {
  type: 'REQUESTS/REQUEST_FAILED';
  payload: {
    id: string;
    error: string;
  };
}
export function requestFailed(id: string, error: string): RequestFailedAction {
  return {
    type: 'REQUESTS/REQUEST_FAILED',
    payload: {
      id,
      error,
    },
  };
}

interface RequestSucceededAction {
  type: 'REQUESTS/REQUEST_SUCCEEDED';
  payload: {
    id: string;
  };
}
export function requestSucceeded(id: string): RequestSucceededAction {
  return {
    type: 'REQUESTS/REQUEST_SUCCEEDED',
    payload: {
      id,
    },
  };
}

interface ClearFailedRequestAction {
  type: 'REQUESTS/CLEAR_FAILED';
  payload: {
    id: string;
  };
}
export function clearFailedRequest(id: string): ClearFailedRequestAction {
  return {
    type: 'REQUESTS/CLEAR_FAILED',
    payload: {
      id,
    },
  };
}

export interface SubmitLoginAction {
  type: 'USER/SUBMIT_LOGIN';
  payload: {
    email: string;
    password: string;
    requestId: string;
  };
}
export function submitLogin(email: string, password: string, requestId: string): SubmitLoginAction {
  return {
    type: 'USER/SUBMIT_LOGIN',
    payload: {
      email,
      password,
      requestId,
    },
  };
}

export interface SubmitForgotPasswordAction {
  type: 'USER/SUBMIT_FORGOT_PASSWORD';
  payload: {
    email: string;
    requestId: string;
  };
}
export function submitForgotPassword(email: string, requestId: string): SubmitForgotPasswordAction {
  return {
    type: 'USER/SUBMIT_FORGOT_PASSWORD',
    payload: {
      email,
      requestId,
    },
  };
}

export interface SubmitResetPasswordAction {
  type: 'USER/SUBMIT_RESET_PASSWORD';
  payload: {
    code: string;
    password: string;
    requestId: string;
  };
}
export function submitResetPassword(code: string, password: string, requestId: string): SubmitResetPasswordAction {
  return {
    type: 'USER/SUBMIT_RESET_PASSWORD',
    payload: {
      code,
      password,
      requestId,
    },
  };
}

export interface SubmitSignupAction {
  type: 'USER/SUBMIT_SIGNUP';
  payload: {
    requestId: string;
    email: string;
    password: string;
  };
}
export function submitSignup(email: string, password: string, requestId: string): SubmitSignupAction {
  return {
    type: 'USER/SUBMIT_SIGNUP',
    payload: {
      requestId,
      email,
      password,
    },
  };
}

interface LoggedInAction {
  type: 'USER/LOGGED_IN';
  payload: User;
}
export function loggedIn(user: User): LoggedInAction {
  return {
    type: 'USER/LOGGED_IN',
    payload: user,
  };
}

interface LogOutAction {
  type: 'USER/LOG_OUT';
}

interface LogOutWithoutNavigationAction {
  type: 'USER/LOG_OUT_WITHOUT_NAVIGATION';
}

/**
 * Trigger a logout.
 */
export function logout(): LogOutAction {
  return {
    type: 'USER/LOG_OUT',
  };
}

/**
 * Trigger a logout without navigation (for custom navigation handling).
 */
export function logoutWithoutNavigation(): LogOutWithoutNavigationAction {
  return {
    type: 'USER/LOG_OUT_WITHOUT_NAVIGATION',
  };
}

interface LoggedOutAction {
  type: 'USER/LOGGED_OUT';
}
export function loggedOut(): LoggedOutAction {
  return {
    type: 'USER/LOGGED_OUT',
  };
}

export interface LoadUserInfoWithTokenAction {
  type: 'USER/GET_INFO_WITH_TOKEN';
  payload: {
    requestId: string;
  };
}
export function getUserInfoWithToken(requestId: string): LoadUserInfoWithTokenAction {
  return {
    type: 'USER/GET_INFO_WITH_TOKEN',
    payload: {
      requestId,
    },
  };
}

interface StoreUserValuesAction {
  type: 'USER/STORE_VALUES';
  payload: {
    values: ValueSet;
  };
}

interface StorePublicUsersAction {
  type: 'USER/STORE_PUBLIC_USERS';
  payload: {
    users: UserPlain[];
  };
}
export function storePublicUsers(users: UserPlain[]): StorePublicUsersAction {
  return {
    type: 'USER/STORE_PUBLIC_USERS',
    payload: {
      users,
    },
  };
}

interface SetToggledComponentAction {
  type: 'USER/SET_TOGGLED_COMPONENT';
  payload: {
    toggledComponents: ToggledUIComponents;
  };
}

export function setToggledComponent(toggledComponents: ToggledUIComponents): SetToggledComponentAction {
  return {
    type: 'USER/SET_TOGGLED_COMPONENT',
    payload: {
      toggledComponents,
    },
  };
}

interface AddToComparisonAction {
  type: 'COMPARE/ADD';
  payload: {
    ref: ModelEntityReference;
  };
}

interface RemoveFromComparisonAction {
  type: 'COMPARE/REMOVE';
  payload: {
    ref: ModelEntityReference;
  };
}

export function setDefaultModelVersionAndRelease(version: string, release: string): SetDefaultReleaseAction {
  return {
    type: 'DEFAULT_VERSION_AND_RELEASE/SET',
    payload: {
      version,
      release,
    },
  };
}
interface SetDefaultReleaseAction {
  type: 'DEFAULT_VERSION_AND_RELEASE/SET';
  payload: {
    version: string;
    release: string;
  };
}

export function setModelVersionAndRelease(version: string, release: string | null): SetModelVersionAndReleaseAction {
  return {
    type: 'MODEL_VERSION_AND_RELEASE/SET',
    payload: {
      version,
      release,
    },
  };
}
interface SetModelVersionAndReleaseAction {
  type: 'MODEL_VERSION_AND_RELEASE/SET';
  payload: {
    version: string;
    release: string | null;
  };
}

export function setValuesSelectorOpen(open: boolean): SetValuesSelectorOpen {
  return {
    type: 'SET_VALUES_SELECTOR_OPEN',
    payload: {
      open,
    },
  };
}
interface SetValuesSelectorOpen {
  type: 'SET_VALUES_SELECTOR_OPEN';
  payload: {
    open: boolean;
  };
}

/**
 * Toggle a single per-feature view setting (PLT-4824). Replaces the legacy
 * `setExpertMode` master action.
 */
export function setViewSetting(key: ViewSettingKey, enabled: boolean): SetViewSetting {
  return {
    type: 'SET_VIEW_SETTING',
    payload: {
      key,
      enabled,
    },
  };
}
interface SetViewSetting {
  type: 'SET_VIEW_SETTING';
  payload: {
    key: ViewSettingKey;
    enabled: boolean;
  };
}

/**
 * Apply a view-settings profile (PLT-4824). Replaces the entire view-settings
 * blob with exactly the listed keys turned on — any key not in the list is
 * turned off. Replaces the legacy `resetViewSettings` action; the equivalent
 * "all off" reset is now expressed as the `all-disabled` profile.
 *
 * Receives the resolved list of keys (rather than the profile object) so the
 * reducer stays free of registry imports and "all" / "none" sentinel
 * handling — `resolveProfileEnabledKeys` is the single source of truth for
 * that translation.
 */
export function applyViewSettingsProfile(enabledKeys: readonly ViewSettingKey[]): ApplyViewSettingsProfile {
  return {
    type: 'APPLY_VIEW_SETTINGS_PROFILE',
    payload: {
      enabledKeys,
    },
  };
}
interface ApplyViewSettingsProfile {
  type: 'APPLY_VIEW_SETTINGS_PROFILE';
  payload: {
    enabledKeys: readonly ViewSettingKey[];
  };
}

/**
 * Open / close the View settings modal (PLT-4824). The modal is mounted once
 * at the app root and listens to this flag, so opening it doesn't require a
 * route change — that's the whole point of the modal-vs-page refactor.
 */
export function setViewSettingsModalOpen(open: boolean): SetViewSettingsModalOpen {
  return {
    type: 'SET_VIEW_SETTINGS_MODAL_OPEN',
    payload: {
      open,
    },
  };
}
interface SetViewSettingsModalOpen {
  type: 'SET_VIEW_SETTINGS_MODAL_OPEN';
  payload: {
    open: boolean;
  };
}

/**
 * Toggle the chat internal-mode debug panel (PLT-4475). Mirrors the
 * expert-mode action above; kept separate so chat debug visibility can be
 * flipped independently of the catch-all expert mode toggle.
 */
export function setChatInternalMode(enabled: boolean): SetChatInternalMode {
  return {
    type: 'SET_CHAT_INTERNAL_MODE',
    payload: {
      enabled,
    },
  };
}
interface SetChatInternalMode {
  type: 'SET_CHAT_INTERNAL_MODE';
  payload: {
    enabled: boolean;
  };
}

/**
 * This action doesn't do anything
 */
interface DummyAction {
  type: 'DUMMY';
}

export type Action =
  | ModalAction
  | ContributionAction
  | DummyAction
  | StoreImpactScoresAction
  | StoreCompaniesAction
  | AddCompaniesAction
  | UpdateCompanyNameAction
  | StorePredictionsDataAction
  | StoreImpactLabelsAction
  | SetValueSetAction
  | SetCertaintyFilterRangeAction
  | SetCorrectLabelFilterAction
  | SetPredictedLabelFilterAction
  | SetSampleOffsetAction
  | SetCertaintyFilterClassifierAction
  | SetSelectedClassifiersAction
  | ClearFailedRequestAction
  | RequestSucceededAction
  | RequestFailedAction
  | RequestStartedAction
  | SubmitLoginAction
  | SubmitSignupAction
  | SubmitForgotPasswordAction
  | SubmitResetPasswordAction
  | LoadUserInfoWithTokenAction
  | StoreUserValuesAction
  | SetToggledComponentAction
  | StorePublicUsersAction
  | LoggedInAction
  | LogOutAction
  | LoggedOutAction
  | RemoveFromComparisonAction
  | AddToComparisonAction
  | SetModelVersionAndReleaseAction
  | SetDefaultReleaseAction
  | StoreGlobalFiguresAction
  | StoreImpactSizeFactorsAction
  | StoreReferenceFiguresAction
  | SetValuesSelectorOpen
  | SetViewSetting
  | ApplyViewSettingsProfile
  | SetViewSettingsModalOpen
  | SetChatInternalMode;
